import type { FC } from "react";
import { useCallback } from "react";
import styled from "styled-components";
import { usePartner } from "../../api/hooks/usePartner";
import {
  useVendorConfigPlugin,
  type VendorConfigPlugin,
} from "../../portals/messagemedia/vendorConfig";
import { useSidebarState } from "../NavigationBar/state";
import { Link } from "../Router/Link";
import { MmCollapsedLogoIcon } from "./MmCollapsedLogoIcon";
import { MmLogoIcon } from "./MmLogoIcon";

import "@nectary/components/skeleton";
import "@nectary/components/skeleton-item";

const LogoContainer = styled.div`
  display: flex;
  padding: 10px 10px 14px 10px;
`;

const LogoLink = styled(Link)<{ isCollapsed: boolean }>`
  display: flex;
  flex: 1;
  height: 40px;
  align-items: center;
  justify-content: ${({ isCollapsed }) =>
    isCollapsed ? "center" : "flex-start"};
  padding: 0 5px;

  :hover {
    border-radius: 8px;
    box-shadow: 1px 2px 6px rgb(10 39 61 / 10%);
  }
`;

const LogoImg = styled.img<{ isCollapsed: boolean }>`
  height: 32px;
  object-fit: contain;
  width: ${({ isCollapsed }) => (isCollapsed ? "32px" : "100%")};
  transition: width 0.3s;
`;

const getLogoUrlPrefix = () => {
  const domain = window.location.hostname;
  const isLocalhost = domain.includes("localhost");
  if (isLocalhost) return "http://localhost:9000/static/logo/";
  return "/hub-container/latest/static/logo/";
};

type Logo = {
  src: string;
  alt: string;
};
type VendorLogo = {
  logo: Logo | undefined;
  collapsedLogo: Logo | undefined;
};
const getVendorLogo = (vendorPlugin: VendorConfigPlugin): VendorLogo => {
  let vendorLogo: VendorLogo = { logo: undefined, collapsedLogo: undefined };
  const collapsedLogo = vendorPlugin?.getByKey("logo-collapsed");
  const logo = vendorPlugin?.getByKey("logo");
  if (
    logo &&
    typeof logo === "object" &&
    typeof logo.src === "string" &&
    typeof logo.alt === "string"
  ) {
    vendorLogo = { ...vendorLogo, logo: logo as VendorLogo["logo"] };
  }

  if (
    collapsedLogo &&
    typeof collapsedLogo === "object" &&
    typeof collapsedLogo.src === "string" &&
    typeof collapsedLogo.alt === "string"
  ) {
    vendorLogo = {
      ...vendorLogo,
      collapsedLogo: collapsedLogo as VendorLogo["collapsedLogo"],
    };
  }
  return vendorLogo;
};

type LogoProps = {
  to: string;
};
export const Logo: FC<LogoProps> = ({ to }) => {
  const state = useSidebarState();
  const partner = usePartner();
  const vendorPlugin = useVendorConfigPlugin();

  const getLogo = useCallback(() => {
    if (partner.status !== "loading") {
      if (
        partner.status === "success" &&
        partner.data &&
        partner.data.portalLogoUrl
      ) {
        /**
         * Partner logo
         */
        return (
          <LogoImg
            isCollapsed={state.isCollapsed}
            src={partner.data.portalLogoUrl}
          />
        );
      }

      /**
       * Vendor logo
       */
      if (vendorPlugin ? !vendorPlugin.isBrandMM() : false) {
        const vendorLogo = getVendorLogo(vendorPlugin);
        if (vendorLogo.logo || vendorLogo.collapsedLogo) {
          const logo = (
            state.isCollapsed
              ? vendorLogo.collapsedLogo || vendorLogo.logo
              : vendorLogo.logo || vendorLogo.collapsedLogo
          ) as Logo;
          return (
            <LogoImg
              isCollapsed={state.isCollapsed}
              alt={logo.alt}
              src={getLogoUrlPrefix() + logo.src}
            />
          ); //Render either logo or vendor logo
        } else {
          console.error("vendorConfig logo is not valid", vendorLogo);
        }
      }

      /**
       * Message media logo
       */
      if (vendorPlugin && vendorPlugin.isBrandMM()) {
        return state.isCollapsed ? <MmCollapsedLogoIcon /> : <MmLogoIcon />;
      }
    }

    return (
      <sinch-skeleton style={{ width: "100%", height: "100%" }}>
        <sinch-skeleton-item size="l" style={{ flex: "1" }} />
      </sinch-skeleton>
    );
  }, [state, vendorPlugin, partner]);

  return (
    <LogoContainer>
      <LogoLink isCollapsed={state.isCollapsed} to={to}>
        {getLogo()}
      </LogoLink>
    </LogoContainer>
  );
};
